import { RemoteRecord } from '@client/common/redux/RemoteRecord';
import { RemoteRecordData, RemoteRecordDataType } from '@client/common/redux/RemoteRecordData';
import { AppState } from '@client/common/redux/store';
import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  B2BRegistrationSubmitRequest,
} from '../api/B2BRegistration.types';
import { UIDApiResponse } from '../services/swissEnterpriseRegisterApi.types';
import { B2BRegistrationStep } from '../utils/constants';

const NAME = 'b2bRegistration';

export interface B2BRegistrationState {
  currentStep: B2BRegistrationStep;
  uidValidation: RemoteRecordDataType<UIDApiResponse>;
  submission: RemoteRecordDataType<null>;
}

const initialState: B2BRegistrationState = {
  currentStep: B2BRegistrationStep.UIDEntry,
  uidValidation: RemoteRecordData.getDefault(),
  submission: RemoteRecordData.getDefault(),
};

export const slice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<B2BRegistrationStep>) => {
      state.currentStep = action.payload;
    },
    validateUIDRequest: (state, action: PayloadAction<string>) => {
      state.uidValidation = RemoteRecordData.init(action.payload);
      return state;
    },
    validateUIDSuccess: (state, action: PayloadAction<UIDApiResponse>) => {
      state.uidValidation = RemoteRecordData.fulfill(action.payload);
      return state;
    },
    validateUIDFailure: (state, action: PayloadAction<{ uid: string; error: ErrorType }>) => {
      state.uidValidation = RemoteRecordData.reject(
        action.payload.error,
        action.payload.uid,
      );
      return state;
    },
    resetUIDValidation: (state) => {
      state.uidValidation = RemoteRecordData.getDefault();
      return state;
    },
    submitFormRequest: (state, _action: PayloadAction<B2BRegistrationSubmitRequest>) => {
      state.submission = RemoteRecordData.init() as RemoteRecordDataType<null>;
      return state;
    },
    submitFormSuccess: (state) => {
      state.submission = RemoteRecordData.fulfill(null) as RemoteRecordDataType<null>;
      return state;
    },
    submitFormFailure: (state, action: PayloadAction<ErrorType>) => {
      state.submission = RemoteRecordData.reject(action.payload) as RemoteRecordDataType<null>;
      return state;
    },
    resetSubmission: (state) => {
      state.submission = RemoteRecordData.getDefault() as RemoteRecordDataType<null>;
      return state;
    },
    resetForm: () => initialState,
  },
});

export const validateUID = createAction<{
  uid: string;
  onSuccess?:(data: UIDApiResponse) => void;
  onFailure?:(error: { message: string; statusCode?: number }) => void;
    }>(`${NAME}/validateUID`);
export const submitForm = createAction<{
  data: B2BRegistrationSubmitRequest;
  onSuccess?:() => void;
    }>(`${NAME}/submitForm`);

export const {
  setCurrentStep,
  validateUIDRequest,
  validateUIDSuccess,
  validateUIDFailure,
  resetUIDValidation,
  submitFormRequest,
  submitFormSuccess,
  submitFormFailure,
  resetSubmission,
  resetForm,
} = slice.actions;

const getState = (state: AppState): B2BRegistrationState => state.b2bRegistration;

export const selectCurrentStep = createSelector(getState, (state) => state.currentStep);

export const selectUIDValidation = createSelector(
  getState,
  (state) => new RemoteRecord<UIDApiResponse>(state.uidValidation),
);

export const selectSubmission = createSelector(
  getState,
  (state) => new RemoteRecord<null>(state.submission),
);

export const selectIsSubmitting = createSelector(
  selectSubmission,
  (submission) => submission.wasStarted && submission.isLoading,
);

export const selectIsValidatingUID = createSelector(
  selectUIDValidation,
  (validation) => validation.wasStarted && validation.isLoading,
);

export const selectSubmissionError = createSelector(
  selectSubmission,
  (submission) => submission.error?.message,
);

export const b2bRegistrationReducer = slice.reducer;
